import { Injectable } from '@angular/core';
import { Router, ActivatedRoute, ParamMap } from '@angular/router';
import { HttpClient, HttpHeaders, HttpParams, HttpBackend } from '@angular/common/http';
import { map } from 'rxjs/operators';
import { OStore, OStoreStart, ObsValueReset, KeyObsValueReset } from '@fireflysemantics/slice';
import { Observable } from 'rxjs';
import { User } from '../../models/user';
import { UserService } from '../user.service';
import { Settings } from '../settings';

/** The user key */
export const USER_KEY = 'USER_KEY';
export const AUTHENTICATION_ERROR_KEY = 'AUTHENTICATION_ERROR_KEY';
export const AUTHENTICATION_ERROR_MESSAGE = 'Invalid username or password';

export interface ISTART extends KeyObsValueReset {
	AUTHENTICATION_ERROR_KEY: ObsValueReset;
	USER_KEY: ObsValueReset;
}

/** Auth Service */
@Injectable({
	providedIn: 'root',
})
export class AuthService {

	START: OStoreStart = {
		AUTHENTICATION_ERROR_KEY: { value: null, reset: null },
		USER_KEY: { value: null, reset: null },
	};

	//CREATE THE OBJECT STORE
	public ostore = new OStore(this.START);

	//REACTIVE STATE OBSERVABLES
	public loggedInUser: User;
	public user$: Observable<User>;
	public isAuthenticated$: Observable<boolean>;
	public authenticationError$: Observable<string | boolean>;
	public authorizePromise: Promise<boolean>;
	public sessionLoadAttempted = false;
	private isSettingsLoaded: boolean = false;

	private http: HttpClient;

	constructor(private router: Router, private userService: UserService, private route: ActivatedRoute, httpBackend: HttpBackend) {
		this.http = new HttpClient(httpBackend);
		this.loadConfig();
		this.initializeStore();

	}
	async loadConfig() {
		const data = await this.http.get('./config/config.xml', { responseType: 'text' }).toPromise();
		if (data) {
			const parser = new DOMParser();
			const xmlData = parser.parseFromString(data, 'application/xml');
			/*
			Settings.getInstance().BASE_URL = xmlData.children[0].getElementsByTagName('BASE_URL')[0].textContent;
			if (Settings.getInstance().BASE_URL.indexOf('{HOST_NAME}') !== -1) {
				Settings.getInstance().BASE_URL = Settings.getInstance().BASE_URL.replace('{HOST_NAME}', window.location.hostname);
			}
			*/
			Settings.getInstance().version = xmlData.children[0].getElementsByTagName('Version')[0].textContent;
			Settings.getInstance().release = xmlData.children[0].getElementsByTagName('Release')[0].textContent;
			Settings.getInstance().lastUpdated = xmlData.children[0].getElementsByTagName('LastUpdated')[0].textContent;

			try {
				const CurrencySymbol: string = xmlData.children[0].getElementsByTagName('CurrencySymbol')[0].textContent;
				if (CurrencySymbol != null) {
					Settings.getInstance().CURRENCY_SYMBOL = CurrencySymbol;
				} else {
					Settings.getInstance().CURRENCY_SYMBOL = '';
				}
			} catch (err) {
				Settings.getInstance().CURRENCY_SYMBOL = '';
			}
			try {
				Settings.getInstance().supportEmailAddress = xmlData.children[0].getElementsByTagName('supportEmailAddress')[0].textContent;
				Settings.getInstance().InactivityTimeoutMinutes = this.parseSettingIntegerValue(xmlData, 'InactivityTimeoutMinutes', 10);
				Settings.getInstance().GlobalSearchResultLimitCases = this.parseSettingIntegerValue(xmlData, 'GlobalSearchResultLimitCases', 200);
				Settings.getInstance().GlobalSearchResultLimitRequests= this.parseSettingIntegerValue(xmlData, 'GlobalSearchResultLimitRequests', 200);
				Settings.getInstance().RequestGridAutoRefreshMinutes = this.parseSettingIntegerValue(xmlData, 'RequestGridAutoRefreshMinutes', 1);
				Settings.getInstance().enableAutoEventEmail = xmlData.children[0].getElementsByTagName('enableAutoEventEmail')[0].textContent;
				Settings.getInstance().EmailOptOutAgencyIds = this.parseSettingIntegerArray(xmlData, 'EmailOptOutAgencyIds');
				Settings.getInstance().AvtarDocumentCategoryId = this.parseSettingIntegerValue(xmlData, 'AvtarDocumentCategoryId', 5);
			} catch (err) {
				console.error("Error processing XML data:", err);
			}
		}
		this.isSettingsLoaded = true;
		//.subscribe(data => {

		//});
	}
	parseSettingIntegerValue(xmlData, settingName, defaultValue) {
		try {
			const value = xmlData.children[0].getElementsByTagName(settingName)[0].textContent;
			return value !== null ? parseInt(value) : defaultValue;
		} catch (err) {
			return defaultValue;
		}
	}
	parseSettingIntegerArray(xmlData, settingName) {
		try {
			const value = xmlData.children[0].getElementsByTagName(settingName)[0].textContent;
			if(value && value.length > 0) {
				return value.split(",").filter(x => x.trim().length && !isNaN(x)).map(Number)
			} else {
				return [];
			}
		} catch (err) {
			return [];
		}
	}

	initializeStore() {
		//INITIALIZE OBJECT STORE STATE
		this.ostore.post(USER_KEY, null);
		this.ostore.post(AUTHENTICATION_ERROR_KEY, false);
		//INITIALIZE THE isAuthenticated$ State
		this.isAuthenticated$ = this.ostore.observe(USER_KEY).pipe(map(u => !!u));
		this.authenticationError$ = this.ostore.observe(AUTHENTICATION_ERROR_KEY);
		this.user$ = this.ostore.observe(USER_KEY);
		this.authorizePromise = null;

	}

	authorize() {
		if (!this.authorizePromise) {

			this.authorizePromise = this.userService.getLoggerInUser().toPromise().then(result => {
				if (result['data'] === undefined || result['data'].user === undefined) {
					return false;
				}
				this.sessionLoadAttempted = true;
				this.loggedInUser = result['data'].user;
				this.loggedInUser.security_elements = result['data'].security_elements || [];				
				Settings.getInstance().loggedInUser = this.loggedInUser;
				this.ostore.put(USER_KEY, result['data'].user);
				this.ostore.put(AUTHENTICATION_ERROR_KEY, false);
				//this.router.navigate(['/projects']);

				this.userService.getAllUsers().subscribe(
					(data) => {
						Settings.getInstance().usersList = data;
					},
					(error) => {
						Settings.getInstance().handleError(error, 'fetching all user records');
					}
				);

				return true;
			})
				.catch((err) => {
					console.log(err);
					this.sessionLoadAttempted = true;
					this.ostore.put(USER_KEY, null);
					this.ostore.put(AUTHENTICATION_ERROR_KEY, false);
					return false;
				});
		}
		return this.authorizePromise;
	}

	login(username: string, password: string) {
		this.authorizePromise = null;
		this.authenticate(username, password).subscribe(
			user => {
				localStorage.setItem('jwt', user['data'].token || '');
				this.loggedInUser = user;
				this.ostore.put(USER_KEY, user);
				this.ostore.put(AUTHENTICATION_ERROR_KEY, false);
				this.router.navigate(['/']);
			},
			(error) => {
				this.ostore.put(AUTHENTICATION_ERROR_KEY, error.error['message']);
			});
	}

	logout() {
		localStorage.removeItem('jwt');
		this.ostore.put(USER_KEY, null);
		Settings.getInstance().user = new User().initializeLongUser();
		Settings.getInstance().loggedInUser = new User().initializeLongUser();		
		this.loggedInUser = null;
		this.authorizePromise = null;
		this.router.navigate(['/login']);
	}

	private authenticate(username: string, password: string) {
		const user = new User();
		user.username = username;
		user.password = password;
		Settings.getInstance().user = user;
		return this.userService.login(username, password);
	}
}
